/**
 * 3D-modeller i kartet via three.js CustomLayerInterface.
 *
 * MapLibre har ingen native modell-lagtype (verifisert mot v6 style-spec:
 * kun fill/line/symbol/circle/heatmap/fill-extrusion/raster/hillshade/
 * color-relief/background). Egne glTF/GLB-modeller rendres derfor med
 * three.js i et custom layer som deler MapLibres WebGL-kontekst.
 *
 * Modell: Aker Brygge klokketårn (clocktower_stylized.glb).
 *   - Enheter: meter. Høyde 9.063 m, grunnflate 2.9×2.9 m.
 *   - +Y opp, front mot +Z. Anker (0,0,0) midt under sokkel, bakkenivå.
 *   (Fra leveransens manifest.json / README.)
 */

import {
  Camera,
  Scene,
  WebGLRenderer,
  DirectionalLight,
  AmbientLight,
  Matrix4,
  Vector3,
} from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { MercatorCoordinate } from 'maplibre-gl';
import { groundElevation } from './terrain.js';

/**
 * Lager et MapLibre custom layer som viser én GLB-modell på et
 * gitt geografisk punkt.
 *
 * @param {object} opts
 * @param {string} opts.id            lag-id
 * @param {string} opts.url           GLB-url (fra /public)
 * @param {[number,number]} opts.lngLat  posisjon
 * @param {number} [opts.altitude=0]  meter over BAKKEN (ikke over havet)
 * @param {number} [opts.rotationDeg=0] rotasjon om vertikal akse (grader)
 * @param {number} [opts.scale=1]     ekstra skalering (1 = ekte meter)
 * @param {number} [opts.minzoom]     vis kun over dette zoomnivået
 * @param {boolean} [opts.snapToTerrain=true] legg modellen på terrenghøyden
 * @returns {import('maplibre-gl').CustomLayerInterface}
 */
export function createModelLayer({
  id,
  url,
  lngLat,
  altitude = 0,
  rotationDeg = 0,
  scale = 1,
  minzoom,
  snapToTerrain = true,
  visible = true,
}) {
  // Modellens origo i Mercator-rommet + meter→mercator-skala.
  //
  // Custom layers drapes IKKE av terrenget slik fill/line/symbol gjør – vi
  // må selv løfte modellen opp til bakkehøyden. Uten dette blir busskuret
  // på Guldbrandslia (105 m o.h.) gravd ned i åsen i det terrenget slås på,
  // mens klokketårnet på Aker Brygge (havnivå) tilfeldigvis overlever.
  let merc = MercatorCoordinate.fromLngLat(lngLat, altitude);
  let meterScale = merc.meterInMercatorCoordinateUnits();
  let groundZ = null;

  let renderer;
  let scene;
  let camera;
  let map;

  // Transform som plasserer/skalerer/roterer modellen i Mercator-rommet.
  // +Y-opp (glTF) → +Z-opp (MapLibre-mercator): roter +90° om X.
  const rotationX = new Matrix4().makeRotationAxis(new Vector3(1, 0, 0), Math.PI / 2);
  // Retning/heading: roter om modellens EGEN opp-akse (Y), FØR X-flippen.
  // (Rotasjon om Z etter X-flippen ville tippet modellen – feil akse.)
  const rotationHeading = new Matrix4().makeRotationAxis(
    new Vector3(0, 1, 0),
    (rotationDeg * Math.PI) / 180,
  );
  // Mercator-Y peker sør; kombinert med X-flippen blir modellen speilvendt.
  // Speil tilbake på modellens egen X-akse (innerst) så orienteringen matcher
  // kildemodellen. Heading kompenseres i rotationDeg.
  const mirror = new Matrix4().makeScale(-1, 1, 1);

  let _visible = visible;

  return {
    id,
    type: 'custom',
    renderingMode: '3d',

    /** Vis/skjul modellen (brukes av avkrysningsboks i preview). */
    setVisible(v) {
      _visible = v;
      if (map) map.triggerRepaint();
    },

    onAdd(mapInstance, gl) {
      map = mapInstance;
      camera = new Camera();
      scene = new Scene();

      // Lys – matcher morgenlyset (varmt, fra sørøst/lavt), lysere.
      const sun = new DirectionalLight(0xffe6c2, 3.4);
      sun.position.set(-0.6, -0.4, 1).normalize(); // fra sørøst, litt over
      scene.add(sun);
      const fill = new DirectionalLight(0xdfe6ea, 1.4);
      fill.position.set(0.5, 0.5, 0.8).normalize();
      scene.add(fill);
      scene.add(new AmbientLight(0xffffff, 0.9));

      // Del MapLibres WebGL-kontekst.
      renderer = new WebGLRenderer({
        canvas: map.getCanvas(),
        context: gl,
        antialias: true,
      });
      renderer.autoClear = false;

      // Last modellen.
      new GLTFLoader().load(
        url,
        (gltf) => {
          scene.add(gltf.scene);
          map.triggerRepaint();
        },
        undefined,
        (err) => console.error(`[model:${id}] kunne ikke laste ${url}`, err),
      );
    },

    render(gl, options) {
      if (!_visible) return;
      // Skjul modellen under minzoom.
      if (minzoom != null && map.getZoom() < minzoom) {
        return;
      }

      // Finn bakken. queryTerrainElevation svarer null til DEM-flisen som
      // dekker punktet er lastet, så vi prøver på nytt hver frame til den
      // gir et tall – og slutter å spørre når vi har det.
      if (snapToTerrain && groundZ === null) {
        const h = groundElevation(map, lngLat);
        if (h !== null) {
          groundZ = h;
          merc = MercatorCoordinate.fromLngLat(lngLat, groundZ + altitude);
          meterScale = merc.meterInMercatorCoordinateUnits();
        }
      }

      // MapLibre v6: matrisen som projiserer mercator-koordinater [0,1]
      // til clip space er defaultProjectionData.mainMatrix (IKKE
      // modelViewProjectionMatrix, som er world→clip i et annet rom).
      const mvp =
        options?.defaultProjectionData?.mainMatrix ??
        options?.modelViewProjectionMatrix ??
        options;
      const m = new Matrix4().fromArray(mvp);

      // Plassér modellen: flytt til origo, skalér meter→mercator, roter.
      const s = meterScale * scale;
      const translate = new Matrix4().makeTranslation(merc.x, merc.y, merc.z);
      const scaleM = new Matrix4().makeScale(s, s, s);

      const model = translate
        .multiply(scaleM)
        .multiply(rotationX)
        .multiply(rotationHeading)
        .multiply(mirror);

      camera.projectionMatrix = m.multiply(model);

      renderer.resetState();
      // Nullstill dybdebufferen for modellen slik at den ikke blir
      // dybde-testet bort av fill-extrusion-byggene (som allerede har
      // fylt dybdebufferen). Dette avgjør også om "usynlig" skyldes depth.
      gl.clear(gl.DEPTH_BUFFER_BIT);
      renderer.render(scene, camera);
      // MERK: ikke kall map.triggerRepaint() her. MapLibre kaller render()
      // selv under animasjoner (flyTo/zoom/pan). En ubetinget triggerRepaint
      // ville laget en evig render-loop som konkurrerer med flyTo og gir
      // hakking. Vi trigger kun én repaint når modellen er ferdig lastet.
    },
  };
}
